// src/pages/HomeServicesPage.jsx
//
// Umbrella landing page for the home_services industry. Path-aware: visiting
// /plumbing, /electrical, /roofing, or /pool swaps niche-specific hero copy
// while sharing the rest of the page (pain points, tool preview, what-you-get,
// FAQ, CTA). All routes navigate to /audit?industry=home_services so the
// backend handles them with the unified home_services PROBLEM_NODES + curated
// must_consider mappings.
//
// Why one page with niche swaps instead of 5 separate pages:
//   - Tools, pain points, and ROI math are 90%+ identical across plumbing,
//     electrical, roofing, pool, pest, painting, landscaping
//   - One landing page → one set of tracking pixels, one A/B testable surface
//   - Niche-specific Google Ads creative can target /plumbing /electrical /etc
//     without forcing a 5x maintenance burden
//   - SEO benefit: each niche route has its own H1/eyebrow but shares the
//     same authoritative depth (links, FAQ, internal anchors)

import React, { useState, useEffect, useMemo } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import SEOHead from "../components/SEOHead";
import Header from "../components/Header";
import Footer from "../components/Footer";
import { Button } from "../components/ui/button";
import {
  ArrowRight,
  CheckCircle,
  Phone,
  Calendar,
  Star,
  TrendingUp,
  AlertTriangle,
  FileText,
  Hammer,
  Zap as ZapIcon,
} from "lucide-react";
import { motion } from "framer-motion";

const fadeUp = {
  hidden: { opacity: 0, y: 16 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.45, ease: "easeOut" } },
};
const stagger = { hidden: {}, visible: { transition: { staggerChildren: 0.07 } } };

// ─── Niche configs ────────────────────────────────────────────────────────────
// Each path maps to a small overlay of niche-specific words. Everything else
// (pain points, tools, ROI numbers, FAQ) is shared because it's universally
// true across home services trades.

const NICHE = {
  default: {
    badge: "Plumbing · Electrical · Roofing · Pool · Pest · Painting · Landscaping",
    heroLine1: "Stop Losing Jobs to",
    heroLine2: "Slow Quotes & Manual Dispatch",
    nicheNoun: "home services business",
    nicheVerb: "service calls",
    seoPath: "/home-services",
    seoTitle: "AI Blueprint for Home Services & Trades — Plumbing, Electrical, Roofing, Pool",
    seoDesc: "Personalized AI implementation blueprint for plumbing, electrical, roofing, pool, pest, and painting businesses. Exact tools, setup order, ROI projections — built from real field-service benchmarks.",
    industryTag: "Home Services",
  },
  plumbing: {
    badge: "Built for plumbing businesses",
    heroLine1: "Stop Losing Plumbing Jobs to",
    heroLine2: "Slow After-Hours Response",
    nicheNoun: "plumbing business",
    nicheVerb: "plumbing calls",
    seoPath: "/plumbing",
    seoTitle: "AI Blueprint for Plumbing Businesses — Capture After-Hours Emergencies",
    seoDesc: "Personalized AI implementation blueprint for plumbing companies. Cut after-hours response time, automate quotes, fill the dispatch board with the right tools — built from field-service benchmarks.",
    industryTag: "Plumbing",
  },
  electrical: {
    badge: "Built for electricians & electrical contractors",
    heroLine1: "Stop Losing Electrical Bids to",
    heroLine2: "Slow Panel-Upgrade Quote Turnaround",
    nicheNoun: "electrical contracting business",
    nicheVerb: "electrical service calls",
    seoPath: "/electrical",
    seoTitle: "AI Blueprint for Electricians & Electrical Contractors — Faster Quotes, Smarter Dispatch",
    seoDesc: "Personalized AI implementation blueprint for electrical contractors. Same-day quotes on panel upgrades, AI dispatch for storm-day demand spikes, automated review capture — built for the trade.",
    industryTag: "Electrical",
  },
  roofing: {
    badge: "Built for roofing contractors",
    heroLine1: "Stop Losing Roofing Bids to",
    heroLine2: "Slow Photo-to-Quote Turnaround",
    nicheNoun: "roofing business",
    nicheVerb: "roofing inspections and bids",
    seoPath: "/roofing",
    seoTitle: "AI Blueprint for Roofing Contractors — Faster Estimates, More Booked Jobs",
    seoDesc: "Personalized AI implementation blueprint for roofing contractors. Photo-to-quote AI, storm-day dispatch, automated insurance documentation, review capture after every job — built for the trade.",
    industryTag: "Roofing",
  },
  pool: {
    badge: "Built for pool service & cleaning businesses",
    heroLine1: "Stop Losing Pool Routes to",
    heroLine2: "Manual Dispatch & Silent Contract Churn",
    nicheNoun: "pool service business",
    nicheVerb: "service routes",
    seoPath: "/pool",
    seoTitle: "AI Blueprint for Pool Service Businesses — Smoother Open/Close Season, Stickier Contracts",
    seoDesc: "Personalized AI implementation blueprint for pool service and cleaning businesses. Auto-renewing maintenance contracts, AI route optimization for open/close season, review capture after every visit.",
    industryTag: "Pool Service",
  },
};

const NICHE_BY_PATH = {
  "/home-services": NICHE.default,
  "/plumbing": NICHE.plumbing,
  "/electrical": NICHE.electrical,
  "/roofing": NICHE.roofing,
  "/pool": NICHE.pool,
};

// ─── Shared content (the same across all niches — true claim is "the
//     pain is universal across trades, the tools too") ────────────────────────

const HERO_BULLETS = [
  "Capture 35–50% of after-hours emergency calls you currently lose to voicemail",
  "Same-day quotes from a tech's photo — turn 2-day estimates into 2-hour ones",
  "AI dispatch cuts the morning routing-by-whiteboard from 90 min to 10 min",
  "Auto-renewing maintenance contracts so recurring revenue doesn't expire silently",
];

const PAIN_POINTS = [
  {
    icon: Phone,
    color: "text-orange-400",
    bg: "bg-orange-500/10",
    border: "border-orange-500/20",
    title: "Emergency calls go to whoever answers fastest",
    desc:
      "Burst pipe at 11pm, blown breaker mid-storm, leak through the ceiling Sunday morning. Customer dials the next shop in Google when you don't pick up. AI receptionists capture 35–50% of after-hours calls that previously hit voicemail.",
    stat: "35–50% of after-hours calls recovered",
  },
  {
    icon: FileText,
    color: "text-yellow-400",
    bg: "bg-yellow-500/10",
    border: "border-yellow-500/20",
    title: "Quotes take 2–3 days, customer picks the same-day shop",
    desc:
      "Re-pipes, panel upgrades, roof replacements, pool resurfaces — all need quick numbers. Photo-to-quote AI compresses the cycle from days to hours so you stop being last in the inbox.",
    stat: "85% faster quote turnaround",
  },
  {
    icon: Calendar,
    color: "text-blue-400",
    bg: "bg-blue-500/10",
    border: "border-blue-500/20",
    title: "Owner spends 60–90 min/morning routing techs by hand",
    desc:
      "Whiteboard route planning. Re-routing on every emergency add-on. Wasted drive time across the territory. AI dispatch in field-service-management software cuts this to under 10 minutes and reduces drive-time waste by 22%.",
    stat: "75% faster dispatch",
  },
  {
    icon: AlertTriangle,
    color: "text-red-400",
    bg: "bg-red-500/10",
    border: "border-red-500/20",
    title: "Tech rolls up missing the right part — 40-min supply run",
    desc:
      "Wrong PEX size, no 50-amp breaker, missing shingles, out of chlorine. First-call resolution under 60% kills your jobs-per-day. Field-service software with truck inventory + job-pipeline forecasting closes this gap.",
    stat: "+18% first-call resolution",
  },
  {
    icon: Star,
    color: "text-purple-400",
    bg: "bg-purple-500/10",
    border: "border-purple-500/20",
    title: "Google reviews come in clusters when you remember to ask",
    desc:
      "Half the satisfied customers never get asked. Auto-text review requests at job completion (when satisfaction peaks) and review volume jumps 3x. Companies with 50+ reviews get 3x more inbound calls.",
    stat: "3x inbound calls",
  },
  {
    icon: TrendingUp,
    color: "text-[#3b82f6]",
    bg: "bg-blue-500/10",
    border: "border-blue-500/20",
    title: "Maintenance contracts expire silently — recurring revenue dies",
    desc:
      "Annual flushes, panel inspections, roof checks, pool open/close all renew on a schedule the spreadsheet can't track. CRM with renewal automation + auto-pay turns maintenance into compounding recurring revenue.",
    stat: "+28% contract renewal rate",
  },
];

const TOOL_PREVIEW = [
  {
    name: "ServiceTitan",
    category: "AI Dispatch & FSM",
    price: "From $398/mo",
    score: 94,
    color: "bg-blue-500",
    why:
      "End-to-end field-service-management for plumbing, electrical, HVAC, and pool. AI dispatch optimization, mobile invoicing, parts management. Industry standard for shops over 5 trucks.",
  },
  {
    name: "Jobber",
    category: "Scheduling + Quoting",
    price: "From $29/mo",
    score: 88,
    color: "bg-cyan-500",
    why:
      "Smaller trade shops use Jobber for automated quotes, scheduling, payment collection. Strong for solo plumbers, electricians, painters scaling to a small crew. 87% of users report faster cash collection.",
  },
  {
    name: "Hatch",
    category: "AI Voice Agent / Receptionist",
    price: "From $149/mo",
    score: 86,
    color: "bg-orange-500",
    why:
      "AI voice agent answers inbound + after-hours calls, triages emergencies, books dispatch tickets straight into ServiceTitan or Jobber. Field-service shops recover 35–50% of calls that used to die in voicemail.",
  },
  {
    name: "NiceJob",
    category: "Review Automation",
    price: "From $75/mo",
    score: 82,
    color: "bg-purple-500",
    why:
      "Auto-texts customers after job completion for Google reviews. Trade shops using NiceJob average 4.8+ stars and 3x inbound call volume vs unmanaged review requests.",
  },
];

const WHAT_YOU_GET = [
  {
    title: "Your exact home services AI tool stack",
    desc: "5–8 tools matched to your trade, team size, and specific problems — with pricing, setup steps, and what to skip. No generic 'top 100 AI tools' list.",
  },
  {
    title: "12-week implementation roadmap",
    desc: "Phase 1 targets your biggest revenue leak — usually after-hours response or quote turnaround. Week-by-week tasks so nothing is overwhelming.",
  },
  {
    title: "Custom prompt library for your trade",
    desc: "15–30 prompts for estimates, customer messages, review requests, technician scripts, service reminders. Ready to paste into ChatGPT or your CRM.",
  },
  {
    title: "ROI projections for your business",
    desc: "Expected after-hours call recovery, faster quote close rate, dispatcher hours saved, contract renewal lift — built from 2026 home-services benchmarks.",
  },
  {
    title: "Risk guide and gotcha warnings",
    desc: "What goes wrong when trade shops install AI tools without a plan. Vendor-stability flags. Integration pitfalls. Keep the techs focused, not fighting software.",
  },
  {
    title: "First 30 days checklist",
    desc: "Day-by-day tasks for your first month — first 10 new Google reviews, first AI-routed dispatch day, first auto-renewing service contract.",
  },
];

const FAQ = [
  {
    q: "I run a multi-trade shop (HVAC + plumbing, or electrical + low-voltage). Does this still work?",
    a: "Yes. The blueprint asks about your primary trade and the secondary services you offer, then tailors tooling that handles cross-trade dispatch and customer history. Multi-trade shops actually benefit MORE because field-service software unifies the customer record across visits.",
  },
  {
    q: "I'm a solo plumber/electrician/roofer. Is this overkill?",
    a: "No. Solo operators get the highest ROI on a tight stack — Jobber + an AI receptionist + automated review capture. The blueprint scales recommendations to team size: solo gets a 3-tool starter, 6-15 person shops get a 5-8 tool stack with AI dispatch, 15+ tech shops get the full ServiceTitan-tier setup.",
  },
  {
    q: "How long does it take to implement?",
    a: "12-week roadmap, designed for owners putting in 2–4 hours per week. Phase 1 hits your biggest revenue leak (typically after-hours response or quote turnaround) and most shops see measurable wins in 2–3 weeks before Phase 2.",
  },
  {
    q: "I've installed an AI tool before and it didn't move the needle. Why is this different?",
    a: "Most shops install a tool before defining the metric it should move, never set up the integrations with QuickBooks/dispatch/CRM, and never compare close rate or first-call-resolution against baseline. The blueprint ranks tools by fit-score for your specific trade + team size, maps integrations to your existing stack, and projects ROI on jobs-per-day before you install anything.",
  },
  {
    q: "What if the recommendations don't fit my business?",
    a: "60-day money-back guarantee, no questions asked. Email within 60 days and we refund in full. You keep the blueprint. We'd rather you leave happy than keep $149 on a report you can't use.",
  },
];

// ─── Page ─────────────────────────────────────────────────────────────────────

const HomeServicesPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [toolCount, setToolCount] = useState(null);
  const [currentPrice, setCurrentPrice] = useState(149);

  // Path-based niche detection. /plumbing → plumbing config. /home-services
  // (or any unmatched path that mounts this component) → default umbrella.
  const niche = useMemo(() => {
    return NICHE_BY_PATH[location.pathname] || NICHE.default;
  }, [location.pathname]);

  const displayCount = toolCount || 240;

  useEffect(() => {
    const base = process.env.REACT_APP_BACKEND_URL || "https://ai-workshop-llc-backend.onrender.com";
    fetch(`${base}/api/tools/stats`)
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => { if (d?.total) setToolCount(d.total); })
      .catch(() => {});
    fetch(`${base}/api/pricing/current`)
      .then((r) => (r.ok ? r.json() : null)
      )
      .then((d) => { if (d?.price) setCurrentPrice(d.price); })
      .catch(() => {});
  }, []);

  const goToAudit = () => navigate("/audit?industry=home_services");

  return (
    <div className="min-h-screen bg-[#0f172a] text-white">
      <SEOHead
        path={niche.seoPath}
        lang="en"
        title={niche.seoTitle}
        description={niche.seoDesc}
        faqItems={FAQ}
        industry={niche.industryTag}
      />
      <Header />

      {/* HERO */}
      <section className="relative pt-20 pb-24 px-4 overflow-hidden">
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute top-0 left-1/4 w-[600px] h-[600px] bg-blue-600/10 rounded-full filter blur-[120px]" />
          <div className="absolute bottom-0 right-1/4 w-[500px] h-[500px] bg-orange-600/10 rounded-full filter blur-[120px]" />
          <div
            className="absolute inset-0 opacity-[0.025]"
            style={{ backgroundImage: "radial-gradient(circle, white 1px, transparent 1px)", backgroundSize: "28px 28px" }}
          />
        </div>
        <div className="max-w-7xl mx-auto relative">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1 }}
                className="inline-flex items-center gap-2 bg-orange-500/10 border border-orange-500/30 text-orange-400 text-sm font-semibold px-4 py-2 rounded-full mb-8"
              >
                <Hammer className="w-4 h-4" />
                {niche.badge}
              </motion.div>

              <motion.h1
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 }}
                className="text-4xl sm:text-5xl lg:text-6xl font-black leading-[1.05] tracking-tight mb-6"
              >
                <span className="text-white">{niche.heroLine1}</span>
                <br />
                <span className="bg-gradient-to-r from-orange-400 via-amber-400 to-yellow-400 bg-clip-text text-transparent">
                  {niche.heroLine2}
                </span>
              </motion.h1>

              <motion.p
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3 }}
                className="text-lg text-slate-400 mb-8 leading-relaxed max-w-xl"
              >
                Answer 8 questions about your {niche.nicheNoun}. Get a personalized blueprint with the{" "}
                <span className="text-white font-semibold">exact tools, setup order, and ROI projections</span> — built from{" "}
                {displayCount}+ vetted AI tools and 2026 field-service benchmarks.
              </motion.p>

              <motion.ul
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4 }}
                className="space-y-3 mb-10"
              >
                {HERO_BULLETS.map((item) => (
                  <li key={item} className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-orange-400 flex-shrink-0 mt-0.5" />
                    <span className="text-slate-300 text-sm">{item}</span>
                  </li>
                ))}
              </motion.ul>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.5 }}
                className="flex flex-col sm:flex-row gap-4 items-start"
              >
                <Button
                  onClick={goToAudit}
                  size="lg"
                  className="bg-orange-500 hover:bg-orange-400 text-slate-950 font-black text-lg px-10 py-6 rounded-2xl shadow-lg shadow-orange-500/30 transition-all hover:scale-[1.02] active:scale-[0.98]"
                >
                  Build My Blueprint — ${currentPrice} <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
                <p className="text-xs text-slate-500 self-center">One-time · No subscription · Ready in minutes</p>
              </motion.div>
            </div>

            {/* Preview card */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.35, duration: 0.6 }}
              className="hidden lg:block"
            >
              <div className="relative bg-gradient-to-br from-slate-900/90 to-slate-950/90 backdrop-blur-xl border border-white/10 rounded-3xl shadow-2xl overflow-hidden">
                <div className="p-5 border-b border-white/8 flex items-center justify-between">
                  <div>
                    <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">AI Implementation Blueprint</div>
                    <div className="text-base text-white font-bold mt-0.5">{niche.industryTag} — Trade Services</div>
                  </div>
                  <div className="bg-blue-500/15 border border-blue-500/30 text-blue-400 text-[10px] font-semibold px-2.5 py-1 rounded-full">
                    ✓ Ready
                  </div>
                </div>
                <div className="grid grid-cols-3 gap-2 p-5 border-b border-white/8">
                  <div className="bg-slate-900/60 rounded-xl p-3 text-center">
                    <div className="text-2xl font-black text-blue-400">186%</div>
                    <div className="text-[9px] text-slate-500 uppercase tracking-wider">Y1 ROI</div>
                  </div>
                  <div className="bg-slate-900/60 rounded-xl p-3 text-center">
                    <div className="text-2xl font-black text-orange-400">22</div>
                    <div className="text-[9px] text-slate-500 uppercase tracking-wider">Hrs saved/wk</div>
                  </div>
                  <div className="bg-slate-900/60 rounded-xl p-3 text-center">
                    <div className="text-2xl font-black text-purple-400">7</div>
                    <div className="text-[9px] text-slate-500 uppercase tracking-wider">Tools</div>
                  </div>
                </div>
                <div className="p-5 space-y-2.5">
                  <div className="text-[10px] text-slate-500 uppercase tracking-widest font-semibold mb-2.5">Top 4 tools for your shop</div>
                  {TOOL_PREVIEW.map((tool) => (
                    <div key={tool.name} className="bg-slate-900/80 rounded-xl p-3 border border-white/8">
                      <div className="flex justify-between items-center mb-2">
                        <span className="text-sm font-semibold text-white">{tool.name}</span>
                        <span className="text-[10px] text-slate-400">{tool.category}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <div className="flex-1 h-1.5 bg-white/10 rounded-full overflow-hidden">
                          <motion.div
                            className={`h-full ${tool.color} rounded-full`}
                            initial={{ width: 0 }}
                            animate={{ width: `${tool.score}%` }}
                            transition={{ duration: 1.2, delay: 0.5, ease: "easeOut" }}
                          />
                        </div>
                        <span className="text-[10px] text-slate-500 flex-shrink-0">{tool.score}% match</span>
                      </div>
                    </div>
                  ))}
                </div>
                <motion.div
                  animate={{ y: [0, -6, 0] }}
                  transition={{ duration: 3, repeat: Infinity, ease: "easeInOut" }}
                  className="absolute -top-3 -right-3 bg-orange-500 text-slate-950 text-xs font-bold px-3 py-1.5 rounded-xl shadow-lg shadow-orange-500/40"
                >
                  ✨ Built in 3 min
                </motion.div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* PAIN POINTS */}
      <section className="py-20 px-4 border-t border-white/8">
        <div className="max-w-6xl mx-auto">
          <motion.div variants={stagger} initial="hidden" whileInView="visible" viewport={{ once: true, margin: "-80px" }}>
            <motion.div variants={fadeUp} className="text-center mb-14">
              <div className="text-sm text-orange-400 font-semibold tracking-widest uppercase mb-3">Sound familiar?</div>
              <h2 className="text-3xl sm:text-4xl font-black text-white mb-4">The 6 Revenue Drains in Most {niche.industryTag} Businesses</h2>
              <p className="text-slate-400 max-w-2xl mx-auto">AI fixes all of these — but only if you know which tools to use and in what order. That's what the blueprint tells you.</p>
            </motion.div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {PAIN_POINTS.map((pp) => {
                const Icon = pp.icon;
                return (
                  <motion.div
                    key={pp.title}
                    variants={fadeUp}
                    className={`rounded-2xl border p-6 ${pp.bg} ${pp.border}`}
                  >
                    <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${pp.bg} border ${pp.border} mb-4`}>
                      <Icon className={`w-5 h-5 ${pp.color}`} />
                    </div>
                    <h3 className="text-white font-bold text-base mb-2">{pp.title}</h3>
                    <p className="text-slate-400 text-sm mb-3 leading-relaxed">{pp.desc}</p>
                    <div className={`text-xs font-bold ${pp.color}`}>{pp.stat}</div>
                  </motion.div>
                );
              })}
            </div>
          </motion.div>
        </div>
      </section>

      {/* TOOL PREVIEW */}
      <section className="py-20 px-4 border-t border-white/8 bg-slate-900/30">
        <div className="max-w-5xl mx-auto">
          <motion.div variants={stagger} initial="hidden" whileInView="visible" viewport={{ once: true, margin: "-80px" }}>
            <motion.div variants={fadeUp} className="text-center mb-14">
              <div className="text-sm text-cyan-400 font-semibold tracking-widest uppercase mb-3">Tool stack preview</div>
              <h2 className="text-3xl sm:text-4xl font-black text-white mb-4">Tools Your Blueprint Could Recommend</h2>
              <p className="text-slate-400 max-w-xl mx-auto">
                Examples — your blueprint picks from {displayCount}+ tools based on your trade, team size, and specific problems.
              </p>
            </motion.div>
            <div className="grid sm:grid-cols-2 gap-5">
              {TOOL_PREVIEW.map((tool) => (
                <motion.div
                  key={tool.name}
                  variants={fadeUp}
                  className="bg-slate-900/70 backdrop-blur-xl border border-white/10 rounded-2xl p-6"
                >
                  <div className="flex items-start justify-between mb-3">
                    <div>
                      <div className="text-white font-bold text-lg">{tool.name}</div>
                      <div className="text-slate-500 text-xs mt-0.5">{tool.category} — {tool.price}</div>
                    </div>
                    <div className="flex items-center gap-1.5 bg-slate-900/60 border border-white/10 rounded-full px-3 py-1">
                      <div className={`w-2 h-2 rounded-full ${tool.color}`} />
                      <span className="text-xs text-slate-400 font-semibold">{tool.score}% match</span>
                    </div>
                  </div>
                  <p className="text-slate-400 text-sm leading-relaxed">{tool.why}</p>
                  <div className="mt-4 h-1.5 bg-white/10 rounded-full overflow-hidden">
                    <motion.div
                      className={`h-full ${tool.color} rounded-full`}
                      initial={{ width: 0 }}
                      whileInView={{ width: `${tool.score}%` }}
                      viewport={{ once: true }}
                      transition={{ duration: 1, delay: 0.3, ease: "easeOut" }}
                    />
                  </div>
                </motion.div>
              ))}
            </div>
            <motion.div variants={fadeUp} className="text-center mt-10">
              <p className="text-slate-500 text-sm">Your blueprint selects 5–8 tools from a database of {displayCount}+, ranked for your specific trade and team.</p>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* WHAT YOU GET */}
      <section className="py-20 px-4 border-t border-white/8">
        <div className="max-w-5xl mx-auto">
          <motion.div variants={stagger} initial="hidden" whileInView="visible" viewport={{ once: true, margin: "-80px" }}>
            <motion.div variants={fadeUp} className="text-center mb-14">
              <div className="text-sm text-purple-400 font-semibold tracking-widest uppercase mb-3">What you get</div>
              <h2 className="text-3xl sm:text-4xl font-black text-white mb-4">Your ${currentPrice} {niche.industryTag} AI Blueprint Includes</h2>
            </motion.div>
            <div className="grid sm:grid-cols-2 gap-5">
              {WHAT_YOU_GET.map((item) => (
                <motion.div
                  key={item.title}
                  variants={fadeUp}
                  className="flex gap-4 p-5 bg-slate-900/70 backdrop-blur-xl border border-white/10 rounded-2xl"
                >
                  <div className="w-8 h-8 rounded-xl bg-orange-500/20 border border-orange-500/30 flex items-center justify-center flex-shrink-0">
                    <CheckCircle className="w-4 h-4 text-orange-400" />
                  </div>
                  <div>
                    <h3 className="text-white font-bold text-sm mb-1">{item.title}</h3>
                    <p className="text-slate-400 text-xs leading-relaxed">{item.desc}</p>
                  </div>
                </motion.div>
              ))}
            </div>
            <motion.p variants={fadeUp} className="text-center text-xs text-slate-500 mt-8 italic">
              60-day money-back guarantee if it doesn't deliver value.
            </motion.p>
          </motion.div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-20 px-4 border-t border-white/8 bg-slate-900/30">
        <div className="max-w-3xl mx-auto">
          <motion.div variants={stagger} initial="hidden" whileInView="visible" viewport={{ once: true, margin: "-80px" }}>
            <motion.div variants={fadeUp} className="text-center mb-12">
              <div className="text-sm text-blue-400 font-semibold tracking-widest uppercase mb-3">Frequently asked</div>
              <h2 className="text-3xl sm:text-4xl font-black text-white">Before you build your blueprint</h2>
            </motion.div>
            <div className="space-y-4">
              {FAQ.map((f, i) => (
                <motion.details
                  key={i}
                  variants={fadeUp}
                  className="group bg-slate-900/70 backdrop-blur-xl border border-white/10 rounded-2xl overflow-hidden"
                >
                  <summary className="cursor-pointer p-5 flex items-center justify-between gap-4 hover:bg-slate-900/90 transition-colors">
                    <span className="text-white font-semibold text-sm">{f.q}</span>
                    <span className="text-slate-500 text-lg transition-transform group-open:rotate-45 flex-shrink-0">+</span>
                  </summary>
                  <div className="px-5 pb-5 -mt-1">
                    <p className="text-slate-400 text-sm leading-relaxed">{f.a}</p>
                  </div>
                </motion.details>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* BOTTOM CTA */}
      <section className="py-20 px-4 border-t border-white/8">
        <div className="max-w-3xl mx-auto text-center">
          <motion.div variants={stagger} initial="hidden" whileInView="visible" viewport={{ once: true, margin: "-80px" }}>
            <motion.h2 variants={fadeUp} className="text-3xl sm:text-5xl font-black text-white mb-2 leading-tight">
              Stop Losing Jobs to Slow Follow-Up.
            </motion.h2>
            <motion.h2 variants={fadeUp} className="text-3xl sm:text-5xl font-black mb-6 leading-tight">
              <span className="bg-gradient-to-r from-orange-400 via-amber-400 to-yellow-400 bg-clip-text text-transparent">
                Run a Smarter {niche.industryTag} Business.
              </span>
            </motion.h2>
            <motion.p variants={fadeUp} className="text-slate-400 mb-10 max-w-xl mx-auto leading-relaxed">
              Answer 8 questions about your {niche.nicheNoun}. Get a blueprint with your exact tool stack, setup order, and week-by-week roadmap. Ready in minutes.
            </motion.p>
            <motion.div variants={fadeUp} className="flex flex-col sm:flex-row gap-3 justify-center items-center">
              <Button
                onClick={goToAudit}
                size="lg"
                className="bg-orange-500 hover:bg-orange-400 text-slate-950 font-black text-lg px-10 py-6 rounded-2xl shadow-lg shadow-orange-500/30 transition-all hover:scale-[1.02] active:scale-[0.98]"
              >
                Build My Blueprint — ${currentPrice} <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
              <p className="text-xs text-slate-500 self-center">Ready in minutes · One-time ${currentPrice} · No subscription</p>
            </motion.div>
          </motion.div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default HomeServicesPage;
